/**
 * Gemini first-pass client.
 *
 * One REST call to `gemini-2.5-flash:generateContent` (v1beta) with the audio
 * inlined as base64 and a `responseSchema` that forces a JSON array of
 * segments. Everything the model returns is treated as untrusted: times are
 * clamped to the real duration, overlaps are trimmed, kinds are validated.
 *
 * The whole point of the prompt is the `heard` vs `contextual` split — see
 * {@link FIRST_PASS_PROMPT}.
 */

import {
  GEMINI_API_KEY,
  GEMINI_API_KEY_HEADER,
  GEMINI_ENDPOINT,
  GEMINI_MODEL,
  hasGeminiKey,
} from '../config';
import { round3 } from '../lib/num';
import type { RawSegment, SegmentKind } from '../types';
import { recordGeminiCall, type GeminiCallLog } from './pipelineLog';

/* ------------------------------------------------------------------ */
/* Errors                                                              */
/* ------------------------------------------------------------------ */

/** Anything that went wrong talking to Gemini, with a UI-ready `message`. */
export class GeminiError extends Error {
  /** HTTP status when the failure came from the API. */
  readonly status?: number;
  /** Google's status string, e.g. `RESOURCE_EXHAUSTED`. */
  readonly code?: string;
  /** Whether retrying the same request is likely to help. */
  readonly retryable: boolean;

  constructor(message: string, options: { status?: number; code?: string; retryable?: boolean } = {}) {
    super(message);
    this.name = 'GeminiError';
    this.status = options.status;
    this.code = options.code;
    this.retryable = options.retryable ?? false;
  }
}

/* ------------------------------------------------------------------ */
/* Limits                                                              */
/* ------------------------------------------------------------------ */

/** Hard ceiling for an inline request body (the API rejects ~20 MB). */
export const MAX_INLINE_BYTES = 14 * 1024 * 1024;

/** Sanity cap so a runaway response can't wedge IndexedDB. */
export const MAX_SEGMENTS = 4000;

/** Shortest segment we keep; anything thinner is noise in the timings. */
const MIN_SEGMENT_SEC = 0.04;

/* ------------------------------------------------------------------ */
/* Prompt                                                              */
/* ------------------------------------------------------------------ */

export const FIRST_PASS_PROMPT = `You are an expert phonetic transcriptionist building a labeled audio dataset.

Listen to the attached audio and segment it into a time-ordered list covering EVERYTHING audible from start to finish. Nothing audible may be skipped: every spoken word, every hesitation sound, every background noise, and every stretch of speech you cannot make out.

One entry per audible unit — one word, one filler, one noise, one unintelligible stretch. Never merge several words into one entry. Never invent audio that is not there. Silence is NOT an entry; simply leave gaps between entries.

For every entry return these fields:

- startSec, endSec: the exact time window of that unit in seconds from the start of the audio, as decimals (e.g. 3.42). endSec must be greater than startSec, and entries must be in ascending time order and must not overlap.

  TIMESTAMP ACCURACY IS CRITICAL — these times are used to cut the audio, so a wrong time produces a clip of the wrong word. Obey all of the following:
  * Measure every time from the ACTUAL AUDIO you are listening to. Never estimate a time from the word's position in the sentence, from how many words came before it, or from an assumed speaking rate.
  * Do NOT emit evenly spaced timestamps. Real speech is irregular: pauses, breaths and word lengths all vary. A list whose entries are a near-constant interval apart is always wrong.
  * startSec is the instant the sound of that unit BEGINS (the onset, not the preceding silence). endSec is the instant it STOPS being audible.
  * Accuracy target: within 0.1 s. Do not let error accumulate as the recording progresses — re-check your position against the audio periodically rather than counting forward from earlier entries, and make sure the final entry ends near the true end of the recording.

- heard: the VERBATIM PHONETIC transcription of what was ACTUALLY produced by the speaker. Write exactly the sounds you hear, NOT what the sentence ought to say. Do not fix grammar, do not fix mispronunciations, do not expand contractions, do not complete words the speaker cut short. If the speaker says "gonna", write "gonna". If they say "libary" for "library", write "libary". If they stutter "th- th- the", that is separate entries. If they slur or swallow a syllable, spell what you hear ("prolly", "wanna", "'bout"). Lowercase unless it is a proper noun. No surrounding punctuation.

- contextual: what that unit most likely IS given the surrounding words — the corrected/intended form ("going to", "library", "the", "about"). If heard and contextual are the same, repeat the same string. For noise or unintelligible audio, use a short plain-English description ("door slam", "unintelligible").

These two fields must be judged independently. NEVER let context change what you write in "heard", and never leave "contextual" as a copy when context clearly implies a different intended word. Reporting the mismatch between them is the entire purpose of this task.

- confidence: your trust in this entry, 0 to 1. Use low values (below 0.4) freely for muffled, overlapping, clipped or guessed audio. Do not default everything to 0.9.

- kind: exactly one of
  - "word"    : an actual spoken word (including a clearly cut-off word)
  - "filler"  : a hesitation or non-lexical vocalisation — umm, hmm, uh, er, ah, mm-hmm, tsk, an audible breath or sigh
  - "noise"   : non-speech background sound — door, cough, keyboard, music, click, traffic, mic bump
  - "unclear" : speech-like audio you genuinely cannot resolve into a word

If the audio is completely silent or contains nothing audible, return an empty list.

Return ONLY the JSON described by the response schema. No commentary, no markdown.`;

/* ------------------------------------------------------------------ */
/* Response schema                                                     */
/* ------------------------------------------------------------------ */

const SEGMENT_SCHEMA = {
  type: 'OBJECT',
  properties: {
    startSec: { type: 'NUMBER', description: 'Start time in seconds from the beginning of the audio.' },
    endSec: { type: 'NUMBER', description: 'End time in seconds. Must be greater than startSec.' },
    heard: { type: 'STRING', description: 'Verbatim phonetic transcription of what was actually heard.' },
    contextual: { type: 'STRING', description: 'The likely intended word given surrounding context.' },
    confidence: { type: 'NUMBER', description: 'Trust score between 0 and 1.' },
    kind: { type: 'STRING', enum: ['word', 'filler', 'noise', 'unclear'] },
  },
  required: ['startSec', 'endSec', 'heard', 'contextual', 'confidence', 'kind'],
  propertyOrdering: ['startSec', 'endSec', 'heard', 'contextual', 'confidence', 'kind'],
} as const;

const RESPONSE_SCHEMA = {
  type: 'OBJECT',
  properties: {
    segments: { type: 'ARRAY', items: SEGMENT_SCHEMA },
  },
  required: ['segments'],
} as const;

/* ------------------------------------------------------------------ */
/* Blob -> base64                                                      */
/* ------------------------------------------------------------------ */

/** Base64 (no data-URL prefix) for an audio Blob, via the native FileReader. */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new GeminiError('Could not read the audio file from storage.'));
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

/** Map a browser MIME type onto one the Gemini inline audio API accepts. */
export function normalizeAudioMime(mime: string | undefined): string {
  const base = (mime ?? '').split(';')[0].trim().toLowerCase();
  switch (base) {
    case 'audio/mpeg':
    case 'audio/mpga':
    case 'audio/mp3':
      return 'audio/mp3';
    case 'audio/x-wav':
    case 'audio/wave':
    case 'audio/vnd.wave':
    case 'audio/wav':
      return 'audio/wav';
    case 'audio/x-m4a':
    case 'audio/m4a':
    case 'audio/mp4':
    case 'audio/aac':
      return 'audio/aac';
    case 'audio/x-flac':
    case 'audio/flac':
      return 'audio/flac';
    case 'audio/ogg':
    case 'audio/opus':
      return 'audio/ogg';
    case 'audio/aiff':
    case 'audio/x-aiff':
      return 'audio/aiff';
    default:
      // We normally send re-encoded WAV, so this is the safest fallback.
      return base.startsWith('audio/') ? base : 'audio/wav';
  }
}

/* ------------------------------------------------------------------ */
/* The call                                                            */
/* ------------------------------------------------------------------ */

export interface FirstPassRequest {
  /** Audio to transcribe. Ideally the mono 16 kHz WAV from `audioSlicer`. */
  audio: Blob;
  /** Real decoded duration; used to clamp the model's timestamps. */
  durationSec: number;
  /** Override the blob's own MIME type. */
  mimeType?: string;
  /** Cancellation (used when a task is deleted mid-flight). */
  signal?: AbortSignal;
  /** Request timeout in ms. Default 5 minutes. */
  timeoutMs?: number;
  /** Extra guidance appended to the standard prompt (e.g. expected language). */
  extraInstructions?: string;
  /** Draw number when the caller re-rolls a bad first pass. Logged, not sent. */
  attempt?: number;
}

interface GeminiPart {
  text?: string;
}
interface GeminiCandidate {
  content?: { parts?: GeminiPart[] };
  finishReason?: string;
}
interface GeminiResponse {
  candidates?: GeminiCandidate[];
  promptFeedback?: { blockReason?: string };
  error?: { message?: string; status?: string; code?: number };
}

interface CallOptions {
  signal?: AbortSignal;
  timeoutMs: number;
  /** Message used when the CALLER's signal aborts the request. */
  cancelMessage: string;
  /** Which pipeline stage this call belongs to. Recorded in the run log. */
  kind: GeminiCallLog['kind'];
  /**
   * Keep the verbatim response text in the run log. First pass only: those are
   * the untouched timestamps, and they are the one thing you cannot reconstruct
   * after the fact. Clip QA answers are one word each and not worth the bytes.
   */
  keepRaw?: boolean;
  /** Draw number, for stages that re-roll. */
  attempt?: number;
}

interface CallResult {
  /** Concatenated text parts, trimmed. May be empty — callers decide. */
  text: string;
  finishReason?: string;
}

/**
 * POST one `generateContent` body and hand back the model's text.
 *
 * Shared by the first pass and the clip-QA loop so both get the same timeout,
 * abort, HTTP-status and safety-block handling. Everything above the transport
 * layer (schema, prompt, parsing) belongs to the caller.
 *
 * Wraps {@link callGeminiRaw} purely to record the attempt — timing, outcome and
 * (for the first pass) the verbatim response — into whatever run log is active.
 * `recordGeminiCall` is a no-op when nothing is being recorded, so this module
 * stays unaware of the pipeline that is calling it.
 */
async function callGemini(body: unknown, options: CallOptions): Promise<CallResult> {
  const startedAt = Date.now();
  try {
    const result = await callGeminiRaw(body, options);
    recordGeminiCall({
      kind: options.kind,
      startedAt,
      ms: Date.now() - startedAt,
      ok: true,
      finishReason: result.finishReason,
      raw: options.keepRaw ? result.text : undefined,
      attempt: options.attempt,
    });
    return result;
  } catch (err) {
    recordGeminiCall({
      kind: options.kind,
      startedAt,
      ms: Date.now() - startedAt,
      ok: false,
      error: err instanceof Error ? err.message : String(err),
      attempt: options.attempt,
    });
    throw err;
  }
}

async function callGeminiRaw(body: unknown, options: CallOptions): Promise<CallResult> {
  const { signal, timeoutMs, cancelMessage } = options;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const onOuterAbort = () => controller.abort();
  signal?.addEventListener('abort', onOuterAbort);

  /*
   * The body is read INSIDE the try, before the timer is cleared.
   *
   * `fetch` resolves as soon as the response headers arrive; the body is still
   * streaming. Clearing the timeout at that point and then awaiting
   * `response.text()` leaves the body read with no deadline and no abort
   * listener — a stalled stream hangs the first pass forever. Observed on this
   * project: first-pass calls sitting at "transcribing" for 15+ minutes against
   * a 5-minute timeout, while the same request from curl answered in under a
   * second. Both awaits must live under the same deadline.
   */
  let response: Response;
  let rawText: string;
  try {
    response = await fetch(GEMINI_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // In a proxy build the key is empty and the header is omitted entirely:
        // the proxy adds the real one. Sending an empty key header would make
        // Google reject the request with a confusing 400.
        ...(GEMINI_API_KEY ? { [GEMINI_API_KEY_HEADER]: GEMINI_API_KEY } : {}),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    rawText = await response.text();
  } catch (err) {
    if (signal?.aborted) throw new GeminiError(cancelMessage);
    if (controller.signal.aborted) {
      throw new GeminiError(
        `Gemini did not answer within ${Math.round(timeoutMs / 1000)}s. Try again, or use a shorter clip.`,
        { retryable: true },
      );
    }
    const detail = err instanceof Error ? err.message : String(err);
    throw new GeminiError(`Could not reach Gemini (${detail}). Check your connection.`, {
      retryable: true,
    });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onOuterAbort);
  }

  let payload: GeminiResponse | null = null;
  try {
    payload = rawText ? (JSON.parse(rawText) as GeminiResponse) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) throw httpError(response.status, payload, rawText);

  if (payload?.promptFeedback?.blockReason) {
    throw new GeminiError(`Gemini refused this audio (${payload.promptFeedback.blockReason}).`);
  }

  const candidate = payload?.candidates?.[0];
  const finishReason = candidate?.finishReason;
  if (finishReason && finishReason !== 'STOP' && finishReason !== 'MAX_TOKENS') {
    throw new GeminiError(`Gemini stopped early (${finishReason}). Try a shorter or cleaner clip.`);
  }

  const text = (candidate?.content?.parts ?? [])
    .map((p) => p.text ?? '')
    .join('')
    .trim();

  return { text, finishReason };
}

/* ------------------------------------------------------------------ */
/* One JSON call, six ways                                             */
/* ------------------------------------------------------------------ */

/**
 * Everything that differs between the JSON calls in this module.
 *
 * Six of the seven Gemini calls here were the SAME forty lines with a
 * different prompt: key guard, empty-audio guard, base64, size guard, build
 * the body, `callGemini`, guard the empty response, strip a ``` fence,
 * `JSON.parse`. Six copies is six chances for one of them to drift — and they
 * had already drifted (one lost its size guard, another its empty-audio
 * guard), which is exactly what this shape makes visible: an omitted `errors`
 * entry now means the guard is skipped, in one place, deliberately.
 *
 * The first pass (`transcribeFirstPass`) does NOT use this: it re-rolls,
 * carries an attempt number, tolerates a truncated response and parses through
 * `parseSegmentsJson`. Forcing it in here would mean options nobody else sets.
 */
interface JsonCallSpec {
  audio: Blob;
  mimeType?: string;
  /** Text part BEFORE the audio. */
  prompt: string;
  /** Text part AFTER the audio, for calls that describe what is in the clip. */
  trailer?: string;
  schema: unknown;
  maxOutputTokens: number;
  /**
   * `thinkingConfig.thinkingBudget`. Omit to leave the model at its default.
   * Every short-answer call sets 0: a deliberating 2.5-flash spends its whole
   * budget thinking and never emits the JSON.
   */
  thinkingBudget?: number;
  timeoutMs: number;
  cancelMessage: string;
  kind: GeminiCallLog['kind'];
  keepRaw?: boolean;
  signal?: AbortSignal;
  /**
   * How this call phrases each failure. A message that is ABSENT means the
   * corresponding guard is not run — see `sentenceWordAlternatives`, which has
   * never checked either of the first two.
   */
  errors: {
    noAudio?: string;
    tooLarge?: string;
    empty: string;
    /** A function when the wording depends on why the model stopped. */
    badJson: string | ((finishReason?: string) => string);
  };
}

/** Models like to wrap JSON in a markdown fence even when told not to. */
function stripJsonFence(text: string): string {
  return text.replace(/^```[a-zA-Z]*\s*/, '').replace(/```\s*$/, '');
}

/** Run one schema-constrained JSON call and hand back the parsed value. */
async function callJson(spec: JsonCallSpec): Promise<unknown> {
  if (!hasGeminiKey) throw new GeminiError('No Gemini API key found.');
  if (spec.errors.noAudio && (!spec.audio || spec.audio.size === 0)) {
    throw new GeminiError(spec.errors.noAudio);
  }

  const base64 = await blobToBase64(spec.audio);
  if (spec.errors.tooLarge && base64.length > MAX_INLINE_BYTES) {
    throw new GeminiError(spec.errors.tooLarge);
  }

  const parts: unknown[] = [
    { text: spec.prompt },
    {
      inline_data: {
        mime_type: normalizeAudioMime(spec.mimeType ?? spec.audio.type),
        data: base64,
      },
    },
  ];
  if (spec.trailer !== undefined) parts.push({ text: spec.trailer });

  const body = {
    contents: [{ role: 'user', parts }],
    generationConfig: {
      temperature: 0,
      responseMimeType: 'application/json',
      responseSchema: spec.schema,
      maxOutputTokens: spec.maxOutputTokens,
      ...(spec.thinkingBudget === undefined
        ? {}
        : { thinkingConfig: { thinkingBudget: spec.thinkingBudget } }),
    },
  };

  const { text, finishReason } = await callGemini(body, {
    signal: spec.signal,
    timeoutMs: spec.timeoutMs,
    cancelMessage: spec.cancelMessage,
    kind: spec.kind,
    keepRaw: spec.keepRaw,
  });

  if (!text) throw new GeminiError(spec.errors.empty, { retryable: true });

  try {
    return JSON.parse(stripJsonFence(text)) as unknown;
  } catch {
    const message =
      typeof spec.errors.badJson === 'function' ? spec.errors.badJson(finishReason) : spec.errors.badJson;
    throw new GeminiError(message, { retryable: true });
  }
}

/**
 * Run the first pass. Returns validated, time-ordered, non-overlapping
 * segments — safe to persist as-is.
 */
export async function transcribeFirstPass(req: FirstPassRequest): Promise<RawSegment[]> {
  if (!hasGeminiKey) {
    throw new GeminiError(
      'No Gemini API key found. Add one to src/config/geminiKey.local.ts and reload.',
    );
  }
  if (!req.audio || req.audio.size === 0) throw new GeminiError('There is no audio to transcribe.');

  const tooLong = () => {
    const minutes = Math.max(1, Math.round(req.durationSec / 60));
    return new GeminiError(
      `This recording is too long to send in one request (~${minutes} min). Split it into shorter files and try again.`,
    );
  };

  // Check the raw size BEFORE base64-encoding: the encoded form is a ~1.33x
  // JS string held entirely in memory, and building a 20 MB one only to throw
  // it away is a good way to get the tab killed on a phone.
  if (req.audio.size * (4 / 3) > MAX_INLINE_BYTES) throw tooLong();

  const base64 = await blobToBase64(req.audio);
  if (base64.length > MAX_INLINE_BYTES) throw tooLong();

  const prompt = req.extraInstructions
    ? `${FIRST_PASS_PROMPT}\n\nAdditional context: ${req.extraInstructions}`
    : FIRST_PASS_PROMPT;

  const body = {
    contents: [
      {
        role: 'user',
        parts: [
          { text: prompt },
          {
            inline_data: {
              mime_type: normalizeAudioMime(req.mimeType ?? req.audio.type),
              data: base64,
            },
          },
          {
            text: `The audio is exactly ${req.durationSec.toFixed(2)} seconds long. Every startSec and endSec must fall inside that range.`,
          },
        ],
      },
    ],
    generationConfig: {
      temperature: 0,
      topP: 0.95,
      responseMimeType: 'application/json',
      responseSchema: RESPONSE_SCHEMA,
      maxOutputTokens: 65536,
    },
  };

  const { text, finishReason: finish } = await callGemini(body, {
    signal: req.signal,
    timeoutMs: req.timeoutMs ?? 5 * 60 * 1000,
    cancelMessage: 'First pass cancelled.',
    kind: 'firstPass',
    keepRaw: true,
    attempt: req.attempt,
  });

  if (!text) {
    throw new GeminiError(
      finish === 'MAX_TOKENS'
        ? 'Gemini ran out of output space before returning anything. Use a shorter clip.'
        : 'Gemini returned an empty response.',
      { retryable: true },
    );
  }

  const truncated = finish === 'MAX_TOKENS';
  const parsed = parseSegmentsJson(text, truncated);
  const segments = validateSegments(parsed, req.durationSec);

  /*
   * A truncated response still parses: `parseSegmentsJson` salvages every
   * complete object it can find. That is the right thing for a response cut off
   * near the end, but it silently turns "Gemini transcribed the first 40% of
   * your file" into a task that looks fully labelled — the user verifies 200
   * clips and exports a dataset covering half the audio, with nothing anywhere
   * saying so. Refuse when the coverage gap is big enough to matter.
   */
  if (truncated && segments.length > 0 && Number.isFinite(req.durationSec) && req.durationSec > 0) {
    const covered = segments[segments.length - 1].endSec;
    if (covered < req.durationSec * 0.9) {
      throw new GeminiError(
        `Gemini ran out of output space after ${covered.toFixed(0)}s of ${req.durationSec.toFixed(0)}s. ` +
          'Split this recording into shorter files and run the first pass on each.',
      );
    }
  }

  return segments;
}

function httpError(status: number, payload: GeminiResponse | null, rawText: string): GeminiError {
  const apiMessage = payload?.error?.message?.trim();
  const code = payload?.error?.status;

  if (status === 400 && /api key/i.test(apiMessage ?? '')) {
    return new GeminiError('That Gemini API key was rejected. Check src/config/geminiKey.local.ts.', {
      status,
      code,
    });
  }
  if (status === 401 || status === 403) {
    return new GeminiError(
      `Gemini denied the request (${status}). The key may be missing permissions or restricted to another origin.`,
      { status, code },
    );
  }
  if (status === 404) {
    return new GeminiError(`Model "${GEMINI_MODEL}" was not found for this API version.`, {
      status,
      code,
    });
  }
  if (status === 413) {
    return new GeminiError('The audio payload was too large for Gemini. Use a shorter file.', {
      status,
      code,
    });
  }
  if (status === 429) {
    return new GeminiError('Gemini rate limit hit. Wait a moment and run the first pass again.', {
      status,
      code,
      retryable: true,
    });
  }
  if (status >= 500) {
    return new GeminiError(`Gemini is unavailable right now (${status}). Try again shortly.`, {
      status,
      code,
      retryable: true,
    });
  }
  const detail = apiMessage || rawText.slice(0, 200) || 'no details';
  return new GeminiError(`Gemini request failed (${status}): ${detail}`, { status, code });
}

/* ------------------------------------------------------------------ */
/* Parsing + validation                                                */
/* ------------------------------------------------------------------ */

const KINDS: readonly SegmentKind[] = ['word', 'filler', 'noise', 'unclear'];

function isKind(value: unknown): value is SegmentKind {
  return typeof value === 'string' && (KINDS as readonly string[]).includes(value);
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function str(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Pull the segment array out of the model's text. Handles the schema shape
 * (`{ segments: [...] }`), a bare array, an accidental markdown fence, and —
 * when the response was truncated — a partially written array.
 */
export function parseSegmentsJson(text: string, truncated = false): unknown[] {
  let body = text.trim();
  if (body.startsWith('```')) {
    body = body.replace(/^```[a-zA-Z]*\s*/, '').replace(/```\s*$/, '').trim();
  }

  const attempt = (candidate: string): unknown[] | null => {
    try {
      const value: unknown = JSON.parse(candidate);
      if (Array.isArray(value)) return value;
      if (value && typeof value === 'object') {
        const segments = (value as { segments?: unknown }).segments;
        if (Array.isArray(segments)) return segments;
      }
      return null;
    } catch {
      return null;
    }
  };

  const direct = attempt(body);
  if (direct) return direct;

  // Truncated (or otherwise malformed) output: salvage every balanced `{...}`
  // that looks like a segment. A stack is required rather than a depth counter,
  // because the wrapper object (`{"segments": [ …`) never closes when the
  // response was cut off — its members still have to be recovered.
  if (truncated || body.includes('{')) {
    const objects: unknown[] = [];
    const starts: number[] = [];
    let inString = false;
    let escaped = false;

    for (let i = 0; i < body.length; i++) {
      const ch = body[i];
      if (inString) {
        if (escaped) escaped = false;
        else if (ch === '\\') escaped = true;
        else if (ch === '"') inString = false;
        continue;
      }
      if (ch === '"') {
        inString = true;
      } else if (ch === '{') {
        starts.push(i);
      } else if (ch === '}') {
        const start = starts.pop();
        if (start === undefined) continue;
        try {
          const obj: unknown = JSON.parse(body.slice(start, i + 1));
          if (obj && typeof obj === 'object' && 'startSec' in (obj as object)) objects.push(obj);
        } catch {
          /* skip malformed fragment */
        }
      }
    }
    if (objects.length > 0) return objects;
  }

  throw new GeminiError('Gemini returned something that was not valid JSON.', { retryable: true });
}

/**
 * Clamp, sort and de-overlap the model's segments.
 *
 * Rules:
 *  - times are clamped into `[0, durationSec]`
 *  - `endSec <= startSec` is repaired to a minimum-length window
 *  - overlapping entries are trimmed against the previous one; entries fully
 *    swallowed by the previous one are dropped
 *  - confidence is clamped to 0..1, kind falls back to a sane guess
 *  - entries with no text at all are dropped
 */
export function validateSegments(input: unknown[], durationSec: number): RawSegment[] {
  const limit = Number.isFinite(durationSec) && durationSec > 0 ? durationSec : Number.MAX_SAFE_INTEGER;

  const cleaned: RawSegment[] = [];

  for (const entry of input.slice(0, MAX_SEGMENTS)) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;

    const rawStart = num(row.startSec ?? row.start ?? row.start_sec);
    const rawEnd = num(row.endSec ?? row.end ?? row.end_sec);
    if (rawStart === null && rawEnd === null) continue;

    let start = Math.max(0, Math.min(limit, rawStart ?? 0));
    let end = Math.max(0, Math.min(limit, rawEnd ?? start + MIN_SEGMENT_SEC));
    if (end <= start) end = Math.min(limit, start + MIN_SEGMENT_SEC);
    if (end <= start) {
      // start sat exactly on the end of the file — pull the window backwards
      start = Math.max(0, limit - MIN_SEGMENT_SEC);
      end = limit;
    }
    if (end <= start) continue;

    const heard = str(row.heard) || str(row.text) || str(row.word);
    const contextual = str(row.contextual) || str(row.context) || heard;
    if (!heard && !contextual) continue;

    const confidenceRaw = num(row.confidence);
    const confidence = confidenceRaw === null ? 0.5 : Math.min(1, Math.max(0, confidenceRaw));

    const kind: SegmentKind = isKind(row.kind) ? row.kind : guessKind(heard);

    cleaned.push({
      startSec: round3(start),
      endSec: round3(end),
      heard: heard || contextual,
      contextual: contextual || heard,
      confidence,
      kind,
    });
  }

  cleaned.sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec);

  const out: RawSegment[] = [];
  for (const seg of cleaned) {
    const prev = out[out.length - 1];
    if (prev) {
      // fully swallowed by the previous window -> duplicate, drop it
      if (seg.endSec <= prev.endSec + 1e-6) continue;
      if (seg.startSec < prev.endSec) seg.startSec = round3(prev.endSec);
      if (seg.endSec - seg.startSec < MIN_SEGMENT_SEC) continue;
    }
    out.push(seg);
  }

  return out;
}

/* ------------------------------------------------------------------ */
/* Clip QA — re-listen to ONE clip with no context                     */
/* ------------------------------------------------------------------ */

/**
 * Deliberately tiny. This prompt is paid for once per clip (hundreds of calls
 * per task), so every sentence has to earn its tokens. The one thing it must
 * hammer is that there is NO context: the whole point of the QA loop is to find
 * clips that only "sound right" because the first pass knew the sentence.
 */
export const CLIP_QA_PROMPT = `This is ONE very short clip cut out of a longer recording. You have NO context — judge only the sound itself.

Transcribe EXACTLY what is audible. It is normally a single word, but may be a hesitation sound, a non-speech noise, or an unintelligible sound.

- Write it in the language and script actually spoken (Arabic speech in Arabic script). Never translate.
- Verbatim: the sounds actually produced, not a corrected or completed word. No punctuation.
- If you hear more than one word, write them all, space-separated.
- text: the transcription. For a non-speech noise, a 1-3 word English description instead. For speech you cannot resolve, "".
- kind: "word", "filler" (umm/hmm/uh/breath), "noise" (non-speech) or "unclear".

Return only the JSON of the response schema.`;

const CLIP_QA_SCHEMA = {
  type: 'OBJECT',
  properties: {
    text: { type: 'STRING', description: 'Verbatim transcription of the clip.' },
    kind: { type: 'STRING', enum: ['word', 'filler', 'noise', 'unclear'] },
  },
  required: ['text', 'kind'],
  propertyOrdering: ['text', 'kind'],
} as const;

export interface ClipQaRequest {
  /** The clip to re-listen to. Keep it small — mono 16 kHz WAV. */
  audio: Blob;
  mimeType?: string;
  signal?: AbortSignal;
  /** Request timeout in ms. Default 60 s — these are one-word clips. */
  timeoutMs?: number;
}

/** What Gemini heard in a clip, with no knowledge of its neighbours. */
export interface ClipQaAnswer {
  text: string;
  kind: SegmentKind;
}

/**
 * Ask Gemini what it hears in a single isolated clip.
 *
 * No `durationSec`, no timestamps, no context: the answer is one short string.
 * `maxOutputTokens` is small and thinking is switched off, because a
 * deliberating 2.5-flash spends its whole budget before emitting the JSON.
 */
export async function transcribeClipInIsolation(req: ClipQaRequest): Promise<ClipQaAnswer> {
  const parsed = await callJson({
    audio: req.audio,
    mimeType: req.mimeType,
    prompt: CLIP_QA_PROMPT,
    schema: CLIP_QA_SCHEMA,
    maxOutputTokens: 256,
    thinkingBudget: 0,
    signal: req.signal,
    timeoutMs: req.timeoutMs ?? 60 * 1000,
    cancelMessage: 'Clip QA cancelled.',
    kind: 'clipQa',
    errors: {
      noAudio: 'There is no clip to check.',
      tooLarge: 'That clip is too large to send for QA.',
      empty: 'Gemini returned nothing for this clip.',
      badJson: 'Gemini returned invalid JSON for this clip.',
    },
  });

  const row = (parsed && typeof parsed === 'object' ? parsed : {}) as Record<string, unknown>;
  const heard = str(row.text ?? row.heard ?? row.transcription);
  return { text: heard, kind: isKind(row.kind) ? row.kind : guessKind(heard) };
}

/* ------------------------------------------------------------------ */
/* Region re-timing — re-cut ONE burst of speech into its items        */
/* ------------------------------------------------------------------ */

/**
 * Why a second timing pass exists at all.
 *
 * Inside a CONTINUOUS burst of speech the first pass's label-to-time allocation
 * is arbitrary: measured on a real Jordanian-Arabic recording, one filler was
 * given 3.08 s of a 2.55 s burst while five neighbouring words were crammed into
 * 60 ms each. Boundary refinement cannot help — there is no silence inside a
 * burst to snap to — and the QA loop can only widen or merge, never redistribute.
 *
 * So we hand the burst back to the model ALONE, with the labels it already
 * produced, and ask for nothing but times. The task is far easier than the first
 * pass: a 1–3 s clip, a handful of known items, no transcription to invent. The
 * prompt spends most of its words on the two failure modes we actually measured
 * (one item swallowing the clip, and slivers) plus the elongated-filler case,
 * where the correct answer really is "this one item is 1.5 s long".
 */
export const REGION_RETIME_PROMPT = `This short clip is ONE continuous stretch of audio cut out of a longer recording. It contains a few consecutive items, listed below in the order they were spoken.

Your only job is TIMING. Give the exact time window of each item inside this clip.

- startSec and endSec are RELATIVE TO THIS CLIP: 0 is the very first sample of the clip you are given.
- Measure from the audio itself. Do NOT spread the items evenly, and do NOT give one item most of the clip while the others get slivers — every item must get a plausible duration of its own.
- A spoken word is rarely shorter than 0.12 s or longer than 1.2 s.
- An elongated filler (ummmm, hmmmm, aaaah, mmmm) may be long, and must include the ENTIRE continuous sound: a filler never ends while the hum is still going.
- Entries must be in ascending time order and must not overlap. Leave gaps where there is silence.
- This clip was cut to contain exactly these items, so together they cover the audible speech in it: the first item starts where the speech starts and the last item ends where the speech stops. Do not bunch every item into the first half and leave the rest of the speech unaccounted for.

Fields:
- sourceIndex: the number of the listed item this entry is. If you hear something that is NOT in the list, return it as well with sourceIndex -1. If a listed item is simply not audible in this clip (it was never there, or it is really part of the item next to it), leave it out of your answer entirely.
- text: what you actually hear for that entry, in the language and script spoken (Arabic speech in Arabic script). Never translate.
- kind: "word", "filler" (umm/hmm/uh/breath), "noise" (non-speech) or "unclear".

Return only the JSON of the response schema.`;

const REGION_ITEM_SCHEMA = {
  type: 'OBJECT',
  properties: {
    sourceIndex: {
      type: 'INTEGER',
      description: 'Number of the listed item, or -1 for something not in the list.',
    },
    text: { type: 'STRING', description: 'What is actually audible for this entry.' },
    kind: { type: 'STRING', enum: ['word', 'filler', 'noise', 'unclear'] },
    startSec: { type: 'NUMBER', description: 'Start time in seconds RELATIVE TO THIS CLIP.' },
    endSec: { type: 'NUMBER', description: 'End time in seconds relative to this clip.' },
  },
  required: ['sourceIndex', 'text', 'kind', 'startSec', 'endSec'],
  propertyOrdering: ['sourceIndex', 'startSec', 'endSec', 'text', 'kind'],
} as const;

const REGION_SCHEMA = {
  type: 'OBJECT',
  properties: {
    items: { type: 'ARRAY', items: REGION_ITEM_SCHEMA },
  },
  required: ['items'],
} as const;

export interface RegionRetimeRequest {
  /** The region audio, cut with no padding. Mono 16 kHz WAV. */
  audio: Blob;
  /** Exact length of `audio`, so the model can be told its own time range. */
  clipDurationSec: number;
  /** The labels currently assigned to this region, in time order. */
  labels: string[];
  /**
   * Where the app's own VAD measured speech inside this clip, in clip-relative
   * seconds. Included because the model reliably front-loads its answer
   * otherwise, and this is a measurement rather than a guess.
   */
  speechStartSec?: number;
  speechEndSec?: number;
  mimeType?: string;
  signal?: AbortSignal;
  /** Request timeout in ms. Default 90 s. */
  timeoutMs?: number;
}

/** One entry of a re-timed region. Times are RELATIVE to the region clip. */
export interface RegionRetimeItem {
  /** Index into the request's `labels`, or -1 when the model heard something new. */
  sourceIndex: number;
  text: string;
  kind: SegmentKind;
  startSec: number;
  endSec: number;
}

/**
 * Ask Gemini to place the given labels inside one short region of audio.
 *
 * Returns exactly what the model said, coerced to the right types but NOT
 * validated: the caller owns the clamping, the ordering rules and the decision
 * to accept or discard the answer, because only it knows where the region sits
 * in the recording and which neighbours must not be disturbed.
 */
export async function retimeRegion(req: RegionRetimeRequest): Promise<RegionRetimeItem[]> {
  if (!hasGeminiKey) throw new GeminiError('No Gemini API key found.');
  if (req.labels.length === 0) throw new GeminiError('A region needs at least one item to place.');

  const list = req.labels.map((label, i) => `${i}: ${label || '(unclear)'}`).join('\n');

  const parsed = await callJson({
    audio: req.audio,
    mimeType: req.mimeType,
    prompt: REGION_RETIME_PROMPT,
    trailer:
      `This clip is exactly ${req.clipDurationSec.toFixed(2)} seconds long; every startSec and endSec must fall inside 0..${req.clipDurationSec.toFixed(2)}.\n` +
      (req.speechStartSec !== undefined && req.speechEndSec !== undefined
        ? `Sound is audible from ${req.speechStartSec.toFixed(2)} to ${req.speechEndSec.toFixed(2)} (measured); the rest of the clip is silence. Your first entry should start near ${req.speechStartSec.toFixed(2)} and your last entry should end near ${req.speechEndSec.toFixed(2)}.\n`
        : '') +
      `It contains these ${req.labels.length} consecutive items:\n${list}`,
    schema: REGION_SCHEMA,
    maxOutputTokens: 2048,
    thinkingBudget: 0,
    signal: req.signal,
    timeoutMs: req.timeoutMs ?? 90 * 1000,
    cancelMessage: 'Region re-timing cancelled.',
    kind: 'retimeRegion',
    errors: {
      noAudio: 'There is no region audio to re-time.',
      tooLarge: 'That region is too large to send for re-timing.',
      empty: 'Gemini returned nothing for this region.',
      badJson: 'Gemini returned invalid JSON for this region.',
    },
  });

  const rows = Array.isArray(parsed)
    ? parsed
    : ((parsed as { items?: unknown })?.items ?? []);
  if (!Array.isArray(rows)) {
    throw new GeminiError('Gemini returned no item list for this region.', { retryable: true });
  }

  const out: RegionRetimeItem[] = [];
  for (const entry of rows.slice(0, MAX_SEGMENTS)) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    const start = num(row.startSec ?? row.start);
    const end = num(row.endSec ?? row.end);
    if (start === null || end === null) continue;
    const idx = num(row.sourceIndex ?? row.index);
    const heard = str(row.text ?? row.heard);
    out.push({
      sourceIndex: idx === null ? -1 : Math.round(idx),
      text: heard,
      kind: isKind(row.kind) ? row.kind : guessKind(heard),
      startSec: start,
      endSec: end,
    });
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Gap fill — what is in this stretch that nothing is labelling?        */
/* ------------------------------------------------------------------ */

/**
 * Why this is NOT {@link REGION_RETIME_PROMPT}.
 *
 * Region re-timing is given a label list and asked only for times; it exists
 * because the first pass knows WHAT was said and not WHEN. This prompt is for
 * the opposite failure, measured on the same 44.8 s recording: 3.41 s of
 * detected speech with no label over it at all, including a full second at
 * 3.56–4.64 s and another at 24.18–25.19 s. There the first pass does not know
 * what was said, so there is no list to place — the clip has to be transcribed
 * from nothing.
 *
 * The one thing it must not do is invent. A gap is often a breath the VAD
 * mistook for speech, and an obliging model asked "what word is this?" will
 * always produce a word. Hence the explicit permission to return nothing, twice.
 */
export const GAP_FILL_PROMPT = `This is a SHORT clip cut out of a longer recording. Something audible in it was missed by an earlier transcription pass.

Transcribe EVERYTHING audible in this clip, in the order you hear it: spoken words, hesitation sounds (umm, hmm, eh), non-speech noises, and stretches of speech you cannot make out. One entry per unit — one word per entry, never a phrase.

- The clip may contain ANY number of entries, including NONE. If all you hear is silence, a breath, or room tone, return an empty list. Do NOT invent a word to fill the clip — a wrong word here is worse than no word.
- startSec and endSec are RELATIVE TO THIS CLIP: 0 is its very first sample. Measure them from the audio, do not space entries evenly.
- text: verbatim, in the language and script actually spoken (Arabic speech in Arabic script — the speaker may be using Jordanian dialect, but any language is possible). Never translate, never correct. For a non-speech noise, a 1-3 word English description. For speech you cannot resolve, "".
- kind: "word", "filler" (umm/hmm/uh/breath), "noise" (non-speech) or "unclear".
- confidence: 0 to 1. Be honest; this audio was already hard enough for one pass to miss it.

Return only the JSON of the response schema.`;

const GAP_ITEM_SCHEMA = {
  type: 'OBJECT',
  properties: {
    startSec: { type: 'NUMBER', description: 'Start time in seconds RELATIVE TO THIS CLIP.' },
    endSec: { type: 'NUMBER', description: 'End time in seconds relative to this clip.' },
    text: { type: 'STRING', description: 'Verbatim transcription of this entry.' },
    kind: { type: 'STRING', enum: ['word', 'filler', 'noise', 'unclear'] },
    confidence: { type: 'NUMBER', description: 'Trust score between 0 and 1.' },
  },
  required: ['startSec', 'endSec', 'text', 'kind', 'confidence'],
  propertyOrdering: ['startSec', 'endSec', 'text', 'kind', 'confidence'],
} as const;

const GAP_SCHEMA = {
  type: 'OBJECT',
  properties: {
    items: { type: 'ARRAY', items: GAP_ITEM_SCHEMA },
  },
  required: ['items'],
} as const;

export interface GapFillRequest {
  /** The gap audio, cut with no padding of its own. Mono 16 kHz WAV. */
  audio: Blob;
  /** Exact length of `audio`, so the model can be told its own time range. */
  clipDurationSec: number;
  /** Where the app's VAD measured speech inside the clip, clip-relative. */
  speechStartSec?: number;
  speechEndSec?: number;
  /** Labels already assigned inside this clip, if any — "do not re-list these". */
  knownLabels?: string[];
  mimeType?: string;
  signal?: AbortSignal;
  /** Request timeout in ms. Default 60 s — these are sub-second clips. */
  timeoutMs?: number;
}

/** One thing heard in a gap clip. Times are RELATIVE to the clip. */
export interface GapFillItem {
  startSec: number;
  endSec: number;
  text: string;
  kind: SegmentKind;
  confidence: number;
}

/**
 * Ask Gemini what is audible in one short clip, with no label list to place.
 *
 * Returns exactly what the model said, coerced to the right types but NOT
 * validated: the caller owns the clamping, the ordering rules and the decision
 * to accept or discard, because only it knows where the clip sits in the
 * recording and which neighbours must not be disturbed. An empty array is a
 * legitimate answer and means "nothing is there".
 */
export async function transcribeGapClip(req: GapFillRequest): Promise<GapFillItem[]> {
  const known = (req.knownLabels ?? []).filter((l) => l.trim());
  const context =
    `This clip is exactly ${req.clipDurationSec.toFixed(2)} seconds long; every startSec and endSec must fall inside 0..${req.clipDurationSec.toFixed(2)}.\n` +
    (req.speechStartSec !== undefined && req.speechEndSec !== undefined
      ? `Sound is audible from ${req.speechStartSec.toFixed(2)} to ${req.speechEndSec.toFixed(2)} (measured); the rest is silence or context from the neighbouring words.\n`
      : '') +
    (known.length > 0
      ? `For reference, these were already transcribed somewhere in this clip, in order: ${known.join(', ')}. List them too if you hear them, in their right places — but the point of this request is whatever else is in there.\n`
      : '');

  const parsed = await callJson({
    audio: req.audio,
    mimeType: req.mimeType,
    prompt: GAP_FILL_PROMPT,
    trailer: context,
    schema: GAP_SCHEMA,
    maxOutputTokens: 2048,
    thinkingBudget: 0,
    signal: req.signal,
    timeoutMs: req.timeoutMs ?? 60 * 1000,
    cancelMessage: 'Gap fill cancelled.',
    kind: 'gapFill',
    errors: {
      noAudio: 'There is no gap audio to transcribe.',
      tooLarge: 'That gap clip is too large to send.',
      // An empty body is an error (the model said nothing at all); an empty
      // ITEM LIST is a valid answer and is handled below without complaint.
      empty: 'Gemini returned nothing for this gap.',
      badJson: 'Gemini returned invalid JSON for this gap.',
    },
  });

  const rows = Array.isArray(parsed) ? parsed : ((parsed as { items?: unknown })?.items ?? []);
  if (!Array.isArray(rows)) return [];

  const out: GapFillItem[] = [];
  for (const entry of rows.slice(0, MAX_SEGMENTS)) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    const start = num(row.startSec ?? row.start);
    const end = num(row.endSec ?? row.end);
    if (start === null || end === null) continue;
    const heard = str(row.text ?? row.heard);
    const conf = num(row.confidence);
    out.push({
      startSec: start,
      endSec: end,
      text: heard,
      kind: isKind(row.kind) ? row.kind : guessKind(heard),
      confidence: conf === null ? 0.5 : Math.min(1, Math.max(0, conf)),
    });
  }
  return out;
}

function guessKind(heard: string): SegmentKind {
  const t = heard.toLowerCase().replace(/[^a-z-]/g, '');
  if (!t) return 'unclear';
  if (/^(u+m+|h+m+|u+h+|e+r+|a+h+|mmhmm|hmm|erm|uhm)$/.test(t)) return 'filler';
  return 'word';
}

/* ------------------------------------------------------------------ */
/* Verbatim transcription — words only, NO timestamps                  */
/* ------------------------------------------------------------------ */

/**
 * The aligned pipeline asks Gemini for the one thing it is reliably good at —
 * WHAT was said — and nothing else. Timestamps come from the local CTC forced
 * aligner afterwards, which measures them physically instead of generating
 * plausible-looking numbers (the root cause of every drift bug this app has
 * had). Removing times from the request also removes the model's main failure
 * surface entirely.
 */
export const VERBATIM_PROMPT = `Transcribe this audio VERBATIM, in the original language and script, as an ordered list of spoken tokens.

Rules:
- One token per item, in the exact order spoken. A token is one word, one filler sound, or one non-speech event.
- Transcribe what you actually HEAR, not what context suggests. Do not correct grammar or dialect. Keep dialect words exactly as pronounced.
- Include EVERY audible token: fillers (umm, hmm, uh, ايه, يعني...) as kind "filler"; coughs/claps/background events as kind "noise"; audible but unintelligible speech as kind "unclear" with your best phonetic guess as text.
- If the same word is repeated, list it every time it occurs.
- Also report the primary spoken language as an ISO 639-3 code (e.g. "ara", "eng", "spa").`;

const VERBATIM_SCHEMA = {
  type: 'OBJECT',
  properties: {
    language: { type: 'STRING' },
    tokens: {
      type: 'ARRAY',
      items: {
        type: 'OBJECT',
        properties: {
          text: { type: 'STRING' },
          kind: { type: 'STRING', enum: ['word', 'filler', 'noise', 'unclear'] },
        },
        required: ['text', 'kind'],
      },
    },
  },
  required: ['language', 'tokens'],
} as const;

export interface VerbatimToken {
  text: string;
  kind: Exclude<SegmentKind, 'silence'>;
}

export interface VerbatimResult {
  /** ISO 639-3 code the model believes the audio is in. */
  language: string;
  /** Every audible token in spoken order. */
  tokens: VerbatimToken[];
}

export interface VerbatimRequest {
  audio: Blob;
  mimeType?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
}

/** Words-only transcription for the aligner-backed pipeline. */
export async function transcribeVerbatim(req: VerbatimRequest): Promise<VerbatimResult> {
  const parsed = await callJson({
    audio: req.audio,
    mimeType: req.mimeType,
    prompt: VERBATIM_PROMPT,
    schema: VERBATIM_SCHEMA,
    maxOutputTokens: 65536,
    // No `thinkingBudget`: this is the one call that benefits from the model
    // deliberating, and it has the token budget to finish afterwards.
    signal: req.signal,
    timeoutMs: req.timeoutMs ?? 5 * 60 * 1000,
    cancelMessage: 'Transcription cancelled.',
    kind: 'verbatim',
    keepRaw: true,
    errors: {
      noAudio: 'There is no audio to transcribe.',
      tooLarge: 'This recording is too large to send in one request.',
      empty: 'Gemini returned nothing.',
      badJson: (finishReason) =>
        finishReason === 'MAX_TOKENS'
          ? 'The transcription was cut off before completing.'
          : 'Gemini returned invalid JSON.',
    },
  });

  const obj = (parsed && typeof parsed === 'object' ? parsed : {}) as Record<string, unknown>;
  const rawTokens = Array.isArray(obj.tokens) ? obj.tokens : [];
  const tokens: VerbatimToken[] = [];
  for (const t of rawTokens) {
    const row = (t && typeof t === 'object' ? t : {}) as Record<string, unknown>;
    const text0 = str(row.text).trim();
    if (!text0) continue;
    const kind = isKind(row.kind) && row.kind !== 'silence' ? row.kind : guessKind(text0);
    tokens.push({ text: text0, kind: kind === 'silence' ? 'unclear' : kind });
  }
  if (tokens.length === 0) throw new GeminiError('Gemini heard no tokens in this audio.', { retryable: true });
  const language = str(obj.language).trim().toLowerCase() || 'ara';
  return { language: /^[a-z]{3}$/.test(language) ? language : 'ara', tokens };
}

/* ------------------------------------------------------------------ */
/* Reading a whole cut — for the WhisperX -> Gemini pipeline           */
/* ------------------------------------------------------------------ */

/**
 * WhisperX decides the cuts; Gemini reads each one. Those cuts are NOT single
 * words — WhisperX groups connected speech, so a clip is often a whole phrase.
 *
 * This exists because {@link CLIP_QA_PROMPT} was reused for that job at first
 * and it opens with "this is ONE very short clip … normally a single word":
 * given a five-second sentence the model returned one word and the rest of the
 * audio lost its label. The prompt below states the opposite expectation.
 */
export const CUT_READ_PROMPT = `This audio clip was cut from a longer recording. It may contain a single word, a hesitation sound, or a whole phrase of several words.

Transcribe EVERYTHING audible in it, in order, as one line.

- Write it in the language and script actually spoken (Arabic speech in Arabic script). Never translate.
- Verbatim: the sounds actually produced, not corrected or completed. Keep dialect forms exactly as pronounced. No punctuation.
- Include hesitation sounds (ايه, امم, اه, um, uh) exactly where they occur.
- Do NOT stop after the first word — the clip usually holds more.
- text: the full transcription. For a non-speech sound, a 1-3 word English description. If nothing is audible, "".
- kind: the clip as a whole — "word" (any speech, one word or many), "filler" (ONLY if the entire clip is hesitation sounds), "noise", or "unclear".

Return only the JSON of the response schema.`;

/**
 * The filler-insisting variant. Gemini reliably transcribes words but tends to
 * tidy hesitations away, which is fatal here: a dropped "ايه" is not just a
 * missing label, it leaves that audio attached to a word that never said it.
 */
export const CUT_READ_PROMPT_FILLERS = `${CUT_READ_PROMPT}

CRITICAL — HESITATION SOUNDS:
This recording is full of hesitation sounds and they are the POINT of the dataset. Never omit, tidy away, shorten or merge them.
- Arabic hesitations to keep verbatim: ايه، اه، امم، همم، اها، يعني (when used as a filler)
- English: um, uh, hmm, er, ah
- Write each one separately, in the exact position it occurs, even mid-sentence and even if repeated.
- An elongated one ("aaaah", "ايييه") is still ONE token — write it once.
- If the clip is ONLY a hesitation sound, set kind to "filler".`;

export interface CutReadRequest {
  audio: Blob;
  /** Use the filler-insisting prompt (the '+filler prompt' engines). */
  emphasizeFillers?: boolean;
  mimeType?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
}

/** Transcribe one cut in full. Same shape as clip QA, different expectation. */
export async function transcribeCut(req: CutReadRequest): Promise<ClipQaAnswer> {
  const parsed = await callJson({
    audio: req.audio,
    mimeType: req.mimeType,
    prompt: req.emphasizeFillers ? CUT_READ_PROMPT_FILLERS : CUT_READ_PROMPT,
    schema: CLIP_QA_SCHEMA,
    // Room for a full phrase, unlike the single-word QA call.
    maxOutputTokens: 2048,
    thinkingBudget: 0,
    signal: req.signal,
    timeoutMs: req.timeoutMs ?? 90 * 1000,
    cancelMessage: 'Cut reading cancelled.',
    kind: 'readCut',
    errors: {
      noAudio: 'There is no clip to read.',
      tooLarge: 'That clip is too large to send.',
      empty: 'Gemini returned nothing for this cut.',
      badJson: 'Gemini returned invalid JSON for this cut.',
    },
  });
  const row = (parsed && typeof parsed === 'object' ? parsed : {}) as Record<string, unknown>;
  const heard = str(row.text ?? row.heard ?? row.transcription);
  return { text: heard, kind: isKind(row.kind) ? row.kind : guessKind(heard) };
}

/* ------------------------------------------------------------------ */
/* Word alternatives — the "correct now" picker                        */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Word alternatives — precomputed during processing                   */
/* ------------------------------------------------------------------ */

/**
 * The Verify screen lets a human tap any word and pick from Gemini's top
 * three guesses. Those guesses are produced HERE, during processing, and
 * stored on the segment — so the correction modal opens instantly instead of
 * making the person wait on a request mid-review.
 *
 * Batched per SENTENCE, not per word: one call covers every word in the run,
 * and the model can hear each word in the context it was actually spoken in,
 * which is exactly the information a single-word clip throws away.
 */
export const SENTENCE_ALTERNATIVES_PROMPT = `You are preparing correction options for a human reviewing a transcription.

You are given the audio of one sentence and the list of words the pipeline currently believes it contains, in order.

For EACH word position, return the three most likely transcriptions of that word, best first.

- Judge the AUDIO. The current guess may be wrong; do not simply repeat it three times.
- Keep the same number of items as the word list, in the same order. Never merge, split, add or drop positions.
- Write in the language and script actually spoken (Arabic speech in Arabic script). Never translate. Keep dialect pronunciation exactly.
- For a hesitation sound, offer hesitation spellings (ايه / اه / امم / همم).
- For a non-speech sound, offer a short English description.
- confidence: 0..1, your own trust in each candidate.

Return only the JSON of the response schema.`;

const SENTENCE_ALT_SCHEMA = {
  type: 'OBJECT',
  properties: {
    words: {
      type: 'ARRAY',
      items: {
        type: 'OBJECT',
        properties: {
          index: { type: 'INTEGER' },
          alternatives: {
            type: 'ARRAY',
            items: {
              type: 'OBJECT',
              properties: {
                text: { type: 'STRING' },
                confidence: { type: 'NUMBER' },
              },
              required: ['text', 'confidence'],
            },
          },
        },
        required: ['index', 'alternatives'],
      },
    },
  },
  required: ['words'],
} as const;

export interface WordAlternative {
  text: string;
  confidence: number;
}

export interface SentenceAlternativesRequest {
  /** Audio of the whole sentence. */
  audio: Blob;
  /** The words in order, as currently labelled. */
  words: string[];
  mimeType?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
}

/** Top-3 candidates for every word of a sentence. Index-aligned with `words`. */
export async function sentenceWordAlternatives(
  req: SentenceAlternativesRequest,
): Promise<WordAlternative[][]> {
  if (req.words.length === 0) return [];
  const list = req.words.map((w, i) => `${i}: ${w}`).join('\n');

  const parsed = await callJson({
    audio: req.audio,
    mimeType: req.mimeType,
    prompt: `${SENTENCE_ALTERNATIVES_PROMPT}\n\nWords (index: current guess):\n${list}`,
    schema: SENTENCE_ALT_SCHEMA,
    maxOutputTokens: 8192,
    thinkingBudget: 0,
    signal: req.signal,
    timeoutMs: req.timeoutMs ?? 90 * 1000,
    cancelMessage: 'Cancelled.',
    kind: 'alternatives',
    errors: {
      // No `noAudio`/`tooLarge`: this call has never guarded either, and adding
      // the guards would change what it throws. Left as found — see F-list.
      empty: 'Gemini returned nothing.',
      badJson: 'Gemini returned invalid JSON.',
    },
  });

  const out: WordAlternative[][] = req.words.map(() => []);
  const rows = Array.isArray((parsed as { words?: unknown[] })?.words)
    ? ((parsed as { words: unknown[] }).words as unknown[])
    : [];
  for (const r of rows) {
    const row = (r && typeof r === 'object' ? r : {}) as Record<string, unknown>;
    const idx = Number(row.index);
    if (!Number.isInteger(idx) || idx < 0 || idx >= out.length) continue;
    const alts = Array.isArray(row.alternatives) ? (row.alternatives as unknown[]) : [];
    for (const a of alts) {
      const ar = (a && typeof a === 'object' ? a : {}) as Record<string, unknown>;
      const t = str(ar.text).trim();
      if (!t) continue;
      const c = Number(ar.confidence);
      out[idx].push({ text: t, confidence: Number.isFinite(c) ? Math.min(1, Math.max(0, c)) : 0.5 });
    }
    out[idx] = out[idx].slice(0, 3);
  }
  return out;
}
